import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/compat/router';
import { useDispatch, useSelector } from 'react-redux';
import { EventType, logAnalyticsEvent } from '@pass-frontend/common/analytics';
import { getRegistrationUrl } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import {
  MemberBigNInput,
  MemberCheckStatus,
  ServiceSectorKey,
} from '@pass-frontend/data-access/common/edge';
import { env } from '@pass-frontend/features/configuration/util';
import {
  Actions,
  EligibilityCheckState,
  MEMBER_ELIGIBILITY_CHECK_BY_BIG_N,
} from '@pass-frontend/features/standalone/data-access';
import { selectBigN } from '@pass-frontend/features/standalone/data-access';

interface EligibilityCheckData {
  memberEligibilityCheckForBigNForStandalone: {
    status: MemberCheckStatus;
    serviceSector: ServiceSectorKey;
    memberCode: string;
  };
}

const serviceSector = env().USER_KEY;
export const useEligibilityCheck = () => {
  const [fetchMemberByEligibility, { loading, data, error }] = useLazyQuery<
    EligibilityCheckData,
    {
      input: MemberBigNInput;
      serviceSector: string;
      email: string;
      marketingConsent: boolean;
    }
  >(MEMBER_ELIGIBILITY_CHECK_BY_BIG_N, {
    fetchPolicy: 'network-only',
  });
  const [isEligibleModalOpen, setIsEligibleModalOpen] = useState(false);
  const [isNotEligibleModalOpen, setIsNotEligibleModalOpen] = useState(false);
  const [memberEligibilityCheckInput, setMemberEligibilityCheckInput] =
    useState<MemberBigNInput>({
      firstName: '',
      lastName: '',
      birthDate: '',
    });
  const { t } = useIntl();
  const router = useRouter();

  const analyticsEventDetails = {
    modal: {
      name: t('notEligibleModal.title'),
    },
    'Trigger UI Element Name': 'we-cant-find-you-modal',
  };

  const eligibilityCheckStatus =
    data?.memberEligibilityCheckForBigNForStandalone?.status;

  const onConfirmModalClose = () => {
    setIsEligibleModalOpen(false);
    setIsNotEligibleModalOpen(false);
  };

  useEffect(() => {
    const showNotEligibleModalForTooManyMatchesOrInternalServerError =
      !isNotEligibleModalOpen &&
      memberEligibilityCheckInput?.zipCode &&
      memberEligibilityCheckInput?.searchId &&
      (eligibilityCheckStatus === MemberCheckStatus.tooManyMatches ||
        eligibilityCheckStatus === MemberCheckStatus.internalServerError);

    if (eligibilityCheckStatus === MemberCheckStatus.matchFound) {
      setIsEligibleModalOpen(true);
      logAnalyticsEvent(EventType.ModalNav, {
        modal: {
          name: t('youAreEligibleModal.title'),
        },
        'Trigger UI Element Name': 'you-are-eligible-modal',
      });
    } else if (showNotEligibleModalForTooManyMatchesOrInternalServerError) {
      setIsNotEligibleModalOpen(true);
      logAnalyticsEvent(EventType.ModalNav, {
        ...analyticsEventDetails,
        'Submitted Zip Code and Health Plan ID': true,
      });
    } else if (
      (eligibilityCheckStatus === MemberCheckStatus.noMatch || error) &&
      !isNotEligibleModalOpen
    ) {
      setIsNotEligibleModalOpen(true);
      logAnalyticsEvent(EventType.ModalNav, analyticsEventDetails);
    }
  }, [eligibilityCheckStatus, !loading]);

  const memberBigNFromUI = useSelector(selectBigN);
  const dispatch = useDispatch();

  return {
    memberCheckInProgress: loading,
    memberCheckError: error,
    fetchMemberByEligibility: (
      input: MemberBigNInput,
      email: string,
      marketingConsent: boolean,
    ) => {
      setMemberEligibilityCheckInput(input);
      fetchMemberByEligibility({
        variables: { input, serviceSector, email, marketingConsent },
      });
    },
    eligibilityCheckStatus: eligibilityCheckStatus,
    registrationUrl: getRegistrationUrl(router?.locale),
    serviceSector:
      data?.memberEligibilityCheckForBigNForStandalone?.serviceSector,
    memberCode:
      data?.memberEligibilityCheckForBigNForStandalone?.memberCode || undefined,
    memberBigNFromUI: memberBigNFromUI, // data that user has entered in eligibility check from and pulled from redux
    updateMemberBigN: (memberBigN: EligibilityCheckState['memberBigN']) => {
      dispatch(Actions.updateMemberBigN(memberBigN));
    },
    isEligibleModalOpen: isEligibleModalOpen,
    isNotEligibleModalOpen: isNotEligibleModalOpen,
    onConfirmModalClose: () => onConfirmModalClose(),
  };
};
